import type { Dayjs } from 'dayjs';
import {
  combineDateAndTime,
  parseShipmentDateTime,
} from '../../common/utils/utils';

export type ShipmentDateFormPair = {
  valueKey: keyof ShipmentLikeForDateFields;
  dateKey: string;
  timeKey: string;
  fieldName: string;
  label: string;
};

/** Minimal shipment shape for edit date/time form load + submit. */
export type ShipmentLikeForDateFields = {
  scheduled_pickup_date?: string | null;
  scheduled_delivery_date?: string | null;
  actual_pickup_date?: string | null;
  actual_delivery_date?: string | null;
};

export const SHIPMENT_DATE_FORM_PAIRS: ShipmentDateFormPair[] = [
  {
    valueKey: 'scheduled_pickup_date',
    dateKey: 'updated_scheduled_pickup_date',
    timeKey: 'updated_scheduled_pickup_date_time',
    fieldName: 'scheduled_pickup_date',
    label: 'scheduled pickup',
  },
  {
    valueKey: 'scheduled_delivery_date',
    dateKey: 'updated_scheduled_delivery_date',
    timeKey: 'updated_scheduled_delivery_date_time',
    fieldName: 'scheduled_delivery_date',
    label: 'scheduled delivery',
  },
  {
    valueKey: 'actual_pickup_date',
    dateKey: 'updated_pickup_date',
    timeKey: 'updated_pickup_date_time',
    fieldName: 'actual_pickup_date',
    label: 'picked up',
  },
  {
    valueKey: 'actual_delivery_date',
    dateKey: 'updated_delivery_date',
    timeKey: 'updated_delivery_date_time',
    fieldName: 'actual_delivery_date',
    label: 'delivered',
  },
];

/**
 * Always returns all date/time keys so switching shipments (same mounted form)
 * clears fields that are empty on the newly loaded shipment.
 */
export function buildShipmentDateFormValues(
  shipment: ShipmentLikeForDateFields,
  dateFormat?: string | null
): Record<string, Dayjs | null> {
  const fields: Record<string, Dayjs | null> = {};
  for (const pair of SHIPMENT_DATE_FORM_PAIRS) {
    const parsed = parseShipmentDateTime(shipment[pair.valueKey], dateFormat);
    fields[pair.dateKey] = parsed?.date ?? null;
    fields[pair.timeKey] = parsed?.time ?? null;
  }
  return fields;
}

export type ApplyShipmentDateEditsResult =
  | { ok: true; fields: Record<string, string | null> }
  | { ok: false; message: string };

/**
 * Builds only changed API date fields for update. Unchanged values are omitted
 * (avoids re-audit). Clearing both pickers when an original existed sends null.
 */
export function applyShipmentDateEdits(
  values: Record<string, unknown>,
  shipment: ShipmentLikeForDateFields,
  dateFormat?: string | null
): ApplyShipmentDateEditsResult {
  const fields: Record<string, string | null> = {};

  for (const pair of SHIPMENT_DATE_FORM_PAIRS) {
    const date = values[pair.dateKey] as Dayjs | undefined | null;
    const time = values[pair.timeKey] as Dayjs | undefined | null;
    const originalValue = shipment[pair.valueKey];

    const originalParsed = parseShipmentDateTime(originalValue, dateFormat);
    const originalCombined = originalParsed
      ? combineDateAndTime(originalParsed.date, originalParsed.time)
      : '';

    if (date || time) {
      if (!date || !time) {
        return {
          ok: false,
          message: `Please select both date and time for ${pair.label}`,
        };
      }
      const combined = combineDateAndTime(date, time);
      if (!combined) {
        return {
          ok: false,
          message: `Please select a valid ${pair.label} date and time`,
        };
      }
      if (combined === originalCombined) {
        continue;
      }
      fields[pair.fieldName] = combined;
    } else if (originalValue) {
      fields[pair.fieldName] = null;
    }
  }

  return { ok: true, fields };
}
